/* eslint-disable no-unused-vars */
import { Backdrop, TextField } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { connect, useSelector } from 'react-redux'
import LoadingBaleomol from '../../../../assets/brand/loading.gif'
import DefaultImage from '../../../../assets/ktp/default-image.png'
import { useAccountVerify } from '../../../../hooks/useAccountVerify'
import useAxiosFunction from '../../../../hooks/useAxiosFunction'
import ActionType from '../../../../redux/reducer/globalActionType'
import { FooterContent, Identity, ImageWrapper, VerifyButton } from './style'
import PhotoUploader from './photo-uploader'

/* === Reusable Input Component === */
const InputField = React.memo(
  ({ label, name, value, onChange, type = 'text', disabled }) => {
    return (
      <TextField
        fullWidth
        variant='outlined'
        margin='normal'
        label={label}
        name={name}
        type={type}
        value={value}
        size='small'
        disabled={disabled}
        onChange={onChange}
        InputLabelProps={{ shrink: true }}
      />
    )
  }
)

const FormVerifyAccount = ({ token, setAccount, reduxAccount }) => {
  const { account } = useSelector(state => state)
  const ktp = account?.data?.ktp

  const isIdentityExists = Boolean(ktp)

  const oldImage = ktp?.image || ''
  const oldNik = ktp?.nik || ''
  const oldName = ktp?.name || ''
  const oldPlace = ktp?.placeOfBirth || ''
  const oldDob = ktp?.dateOfBirth || ''
  const status = ktp?.status || ''

  const isVerifying = status === 'verify'

  const { isModalOpen, setIsModalOpen } = useAccountVerify()
  const { dispatchData } = useAxiosFunction()

  const [isUploading, setIsUploading] = useState(false)
  const [displayImage, setDisplayImage] = useState(oldImage || DefaultImage)

  /* === Memoized Initial Data (ESLint OK) === */
  const initialData = useMemo(
    () => ({
      picture: '',
      nik: oldNik,
      name: oldName,
      placeOfBirth: oldPlace,
      dateOfBirth: oldDob?.split('T')[0] || '',
    }),
    [oldNik, oldName, oldPlace, oldDob]
  )

  const [form, setForm] = useState(initialData)

  const [isFilled, setIsFilled] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [message, setMessage] = useState('')
  const alertRef = useRef()

  /* === Image Handler === */
  const changeHandler = useCallback(event => {
    const file = event.target.files?.[0]

    if (file) {
      setDisplayImage(URL.createObjectURL(file))
      setForm(prev => ({ ...prev, picture: file }))
      return
    }

    setDisplayImage(DefaultImage)
    setForm(prev => ({ ...prev, picture: '' }))
  }, [])

  /* === Text Handler === */
  const handleTextChange = useCallback(event => {
    const { name, value } = event.target

    if (name === 'dateOfBirth') {
      const normalized = (value?.split('T')[0] || value).trim()
      setForm(prev => ({ ...prev, dateOfBirth: normalized }))
      return
    }

    setForm(prev => ({ ...prev, [name]: value }))
  }, [])

  /* === Check if ANY value has changed === */
  const hasChanged = useMemo(() => {
    return (
      form.nik !== initialData.nik ||
      form.name !== initialData.name ||
      form.placeOfBirth !== initialData.placeOfBirth ||
      form.dateOfBirth !== initialData.dateOfBirth ||
      form.picture !== initialData.picture
    )
  }, [
    form.nik,
    form.name,
    form.placeOfBirth,
    form.dateOfBirth,
    form.picture,
    initialData,
  ])

  /* === Submit Enabled Only When Changed + Filled === */
  useEffect(() => {
    const ready =
      form.nik.trim() &&
      form.name.trim() &&
      form.placeOfBirth.trim() &&
      form.dateOfBirth.trim()

    setIsFilled(Boolean(ready && hasChanged))
  }, [form, hasChanged])

  /* === Submit === */
  const submit = async () => {
    try {
      const formData = new FormData()
      formData.append('file', form.picture)
      formData.append('nik', form.nik)
      formData.append('name', form.name)
      formData.append('dateOfBirth', form.dateOfBirth)
      formData.append('placeOfBirth', form.placeOfBirth)

      setIsUploading(true)
      setIsError(false)
      setIsSuccess(false)

      const response = await dispatchData({
        url: `/ktp`,
        method: 'post',
        credentials: token,
        data: formData,
      }).catch(err => err.response)

      const message = response?.data?.message
      if (response?.status > 300) throw new Error(message || 'Error occured')
      const data = response?.data?.data || {}
      setAccount({
        data: { ...reduxAccount.data, ktp: data },
        updated: Date.now(),
      })

      setMessage(message)
      setIsModalOpen(false)
      setIsSuccess(true)
    } catch (err) {
      setIsError(true)
      setMessage(err.message || 'Error ketika proses upload!')
    } finally {
      setIsUploading(false)
    }
  }

  /* === Scroll Alert === */
  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isError, isSuccess])

  return (
    <Identity>
      {isUploading && (
        <Backdrop open style={{ zIndex: 99 }}>
          <img src={LoadingBaleomol} alt='loading' />
        </Backdrop>
      )}

      {isIdentityExists && (
        <form>
          <InputField
            label='NIK'
            name='nik'
            value={form.nik}
            onChange={handleTextChange}
            disabled={isVerifying}
          />

          <InputField
            label='Nama Lengkap'
            name='name'
            value={form.name}
            onChange={handleTextChange}
            disabled={isVerifying}
          />

          <InputField
            label='Tempat Lahir'
            name='placeOfBirth'
            value={form.placeOfBirth}
            onChange={handleTextChange}
            disabled={isVerifying}
          />

          <InputField
            label='Tanggal Lahir'
            name='dateOfBirth'
            type='date'
            value={form.dateOfBirth}
            onChange={handleTextChange}
            disabled={isVerifying}
          />

          <ImageWrapper>
            <PhotoUploader
              preview={displayImage}
              onChange={changeHandler}
              disabled={isVerifying}
              onlyPreview={isVerifying}
            />
          </ImageWrapper>

          {!isUploading && isError && (
            <Alert ref={alertRef} severity='error'>
              {message}
            </Alert>
          )}

          {!isUploading && isSuccess && (
            <Alert ref={alertRef} severity='success'>
              {message}
            </Alert>
          )}

          {!isVerifying && (status === '' || status === 'reject') && (
            <FooterContent>
              <VerifyButton
                primary
                component='span'
                disabled={!isFilled}
                onClick={submit}
              >
                Submit
              </VerifyButton>
            </FooterContent>
          )}
        </form>
      )}

      {!isIdentityExists && (
        <Alert severity='info'>
          Sepertinya session anda usang, silahkan logout dan login kembali.
        </Alert>
      )}
    </Identity>
  )
}

const mapDispatchToProps = dispatch => ({
  setAccount: account =>
    dispatch({
      type: ActionType.SET_ACCOUNT,
      payload: { ...account },
    }),
})

const mapStateToProps = state => ({
  token: state.token,
  reduxAccount: state.account,
})

export default connect(mapStateToProps, mapDispatchToProps)(FormVerifyAccount)
